import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useChartDateRange } from 'hooks';
import './CallStatusDonut.scss';

interface CallQualificationData {
    disposition?: string;
    date?: string;
}

interface CallQualificationDistributionBarGraphProps {
    data?: CallQualificationData[];
    title?: string;
    amountofCalls?: number;
    height?: number;
    dateRangeType?: string;
    startDate?: Date;
    endDate?: Date;
}

interface DispositionGroup {
    name: string;
    value: number;
    color: string;
}

interface TimeGroup extends Record<string, number | string> {
    name: string;
    total: number;
    sortKey: string;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        dataKey: string;
        value: number;
        color: string;
    }>;
    label?: string;
}

/**
 * A bar graph displaying call qualification distribution by disposition or time period
 */
function CallQualificationDistributionBarGraph({
    data = [],
    title = 'Call Qualification Distribution',
    amountofCalls = 0,
    height = 100,
    dateRangeType = 'today',
    startDate,
    endDate,
}: CallQualificationDistributionBarGraphProps): React.JSX.Element | null {
    // Use the shared chart date range hook
    const {
        dateRangeDisplay,
        shouldGroupByType,
        groupingStrategy,
        getTimeGroupInfo,
        initializeTimeGroups,
        isDispositionView,
    } = useChartDateRange({ dateRangeType, startDate, endDate });

    // Define color palette for different dispositions
    const dispositionColors: Record<string, string> = {
        qualified: '#4CAF50',
        disqualified: '#F44336',
        undetermined: '#FF9800',
        interested: '#2196F3',
        not_interested: '#9C27B0',
        callback: '#673AB7',
        voicemail: '#607D8B',
        busy: '#795548',
        no_answer: '#E91E63',
        transfer: '#FFC107',
        unknown: '#9E9E9E',
        other: '#9E9E9E',
    };

    // Get all unique dispositions from the data
    const allDispositions = useMemo(() => {
        const dispositionSet = new Set<string>();
        let hasCallsWithoutDisposition = false;

        data.forEach((call) => {
            if (call.disposition) {
                const disposition = call.disposition.toLowerCase();
                dispositionSet.add(disposition);
            } else {
                hasCallsWithoutDisposition = true;
            }
        });

        const dispositions = Array.from(dispositionSet);
        if (hasCallsWithoutDisposition) {
            dispositions.push('other');
        }

        return dispositions;
    }, [data]);

    // Normalize disposition name for display
    const normalizeDispositionName = (disposition?: string): string => {
        if (!disposition || disposition === 'unknown') {
            return 'other';
        }
        const normalized = disposition.toLowerCase();
        if (normalized === 'qualify') {
            return 'qualified';
        } else if (normalized === 'disqualified') {
            return 'disqualified';
        } else if (normalized === 'undetermined') {
            return 'undetermined';
        } else {
            return normalized;
        }
    };

    // Get display name for disposition
    const getDispositionDisplayName = (disposition?: string): string => {
        if (!disposition || disposition === 'other' || disposition === 'unknown') {
            return 'Other';
        }
        const normalized = normalizeDispositionName(disposition);
        return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace('_', ' ');
    };

    // Process the raw API data based on date range type
    const processedData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        if (shouldGroupByType) {
            // Group by disposition categories (dynamically based on actual data)
            const dispositionGroups: Record<string, DispositionGroup> = {};

            data.forEach((call) => {
                const disposition = normalizeDispositionName(call.disposition);
                const displayName = getDispositionDisplayName(call.disposition);

                if (!dispositionGroups[disposition]) {
                    dispositionGroups[disposition] = {
                        name: displayName,
                        value: 0,
                        color: dispositionColors[disposition] || '#8884d8',
                    };
                }

                dispositionGroups[disposition].value += 1;
            });

            // Convert to array and sort by value (highest first)
            return Object.values(dispositionGroups).sort((a, b) => b.value - a.value);
        } else {
            // Group by time periods for longer date ranges
            const timeGroups: Record<string, TimeGroup> = {};
            const initializeGroup = initializeTimeGroups(allDispositions, normalizeDispositionName);

            data.forEach((call) => {
                if (!call.date) return;

                const timeGroupInfo = getTimeGroupInfo(call.date, groupingStrategy);
                if (!timeGroupInfo) return;

                const { groupKey, displayName } = timeGroupInfo;

                if (!timeGroups[groupKey]) {
                    timeGroups[groupKey] = initializeGroup(groupKey, displayName) as TimeGroup;
                }

                timeGroups[groupKey].total += 1;

                // Count each disposition (including 'other' for calls without disposition)
                const normalized = normalizeDispositionName(call.disposition);
                timeGroups[groupKey][normalized] = ((timeGroups[groupKey][normalized] as number) || 0) + 1;
            });

            // Convert to array and sort by date
            return Object.values(timeGroups).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
        }
    }, [
        data,
        dateRangeType,
        startDate,
        endDate,
        allDispositions,
        shouldGroupByType,
        groupingStrategy,
        getTimeGroupInfo,
        initializeTimeGroups,
    ]);

    if (!data || data.length === 0) {
        return null;
    }

    // Custom tooltip for time-based view
    const CustomTooltip = ({ active, payload, label }: CustomTooltipProps): React.JSX.Element | null => {
        if (active && payload && payload.length) {
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                >
                    <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '8px 0', color: entry.color }}>
                            {`${getDispositionDisplayName(entry.dataKey)}: ${entry.value}`}
                        </p>
                    ))}
                    <p style={{ fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                        {`Total: ${payload.reduce((sum, entry) => sum + entry.value, 0)}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='metric-card'>
            <div className='metric-header'>
                <h3>{title}</h3>
                <span className='metric-value'>{`${amountofCalls} calls`}</span>
                {dateRangeDisplay && (
                    <div className='date-range-display' style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {dateRangeDisplay}
                    </div>
                )}
            </div>
            <div className='chart-wrapper'>
                <ResponsiveContainer width='100%' height='100%'>
                    <BarChart data={processedData} margin={{ top: 5, right: 5, left: 20, bottom: 5 }}>
                        <XAxis
                            dataKey='name'
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                            tick={{ fontSize: 10 }}
                            angle={isDispositionView ? 0 : -45}
                            textAnchor={isDispositionView ? 'middle' : 'end'}
                            height={isDispositionView ? 30 : 60}
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} width={30} />

                        {isDispositionView ? (
                            <Tooltip
                                formatter={(value) => [value, 'Calls']}
                                labelFormatter={(label) => `${label}:`}
                                contentStyle={{
                                    textAlign: 'left',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '8px',
                                }}
                                wrapperStyle={{ zIndex: 1000 }}
                            />
                        ) : (
                            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                        )}

                        {isDispositionView ? (
                            <Bar dataKey='value' fill='#8884d8' radius={[2, 2, 0, 0]} />
                        ) : (
                            allDispositions.map((disposition, index) => {
                                const normalized = normalizeDispositionName(disposition);
                                const color =
                                    dispositionColors[normalized] ||
                                    `#${Math.floor(Math.random() * 16777215).toString(16)}`;

                                return (
                                    <Bar
                                        key={normalized}
                                        dataKey={normalized}
                                        fill={color}
                                        radius={[2, 2, 0, 0]}
                                        name={getDispositionDisplayName(disposition)}
                                    />
                                );
                            })
                        )}

                        {!isDispositionView && <Legend />}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default CallQualificationDistributionBarGraph;
